import RefreshButton from '@/components/RefreshButton';
import {
  useGetPerSheetQuery,
  useUpdatePerSheetBulkMutation,
  type DailyPayEntry,
  type DailyPayRow,
  type PerSheetDayUpdate,
} from '@/app/features/employee/emp-payroll';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilEdit02Icon,
  SearchIcon,
  UploadIcon,
} from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Flex, Input, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import PaySheetGrid from './_components/PaySheet/PaySheetGrid';
import PaySheetUpload from './_components/PaySheet/PaySheetUpload';

/** The grid shows one page of employees at a time; the API sends them all. */
const PAGE_SIZE = 10;

const LOCKED_HINT = 'This cycle has not started yet';

/**
 * Pay Sheet: one present / absent answer per daily employee per day, edited on
 * screen or through an Excel upload. Monthly employees are never on it.
 */
const PaySheet = () => {
  /** The month being viewed. The server hands back its cycles. */
  const [month, setMonth] = useState(dayjs());
  /**
   * Which cycle of that month: an index, `'last'` after stepping back into a
   * month, or `null` for the cycle containing today.
   */
  const [cyclePos, setCyclePos] = useState<number | 'last' | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  /** The sheet opens read-only; editing is entered explicitly. */
  const [isEditing, setIsEditing] = useState(false);
  /** Unsaved cell edits, keyed `user_id|YYYY-MM-DD`. Nothing is sent until Save. */
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const [openUpload, setOpenUpload] = useState(false);

  const { data, isLoading, isFetching } = useGetPerSheetQuery({
    payBasis: 'DAILY',
    year: month.year(),
    month: month.format('MM'),
  });

  const [saveSheet, { isLoading: isSaving }] = useUpdatePerSheetBulkMutation();

  const sheet = data?.data;
  const cycles = sheet?.cycles || [];
  const todayIndex = cycles.findIndex(
    (cycle) =>
      !dayjs().isBefore(cycle.startDate, 'day') &&
      !dayjs().isAfter(cycle.endDate, 'day'),
  );
  const activeIndex =
    cyclePos === 'last'
      ? cycles.length - 1
      : (cyclePos ?? Math.max(todayIndex, 0));
  const activeCycle = cycles[activeIndex] || cycles[0];

  /**
   * A cycle can be changed once it has started: the current one and every one
   * before it. Future cycles stay read only. A past cycle whose payroll is
   * already generated is refused by the server, which owns that rule.
   */
  const canChange =
    Boolean(activeCycle) && !dayjs().isBefore(activeCycle.startDate, 'day');
  const isEditMode = isEditing && canChange;

  const dates = (sheet?.dates || []).filter((date) =>
    activeCycle
      ? !dayjs(date).isBefore(activeCycle.startDate, 'day') &&
        !dayjs(date).isAfter(activeCycle.endDate, 'day')
      : true,
  );
  const days = dates.map((date) => dayjs(date));

  // The API answers with present/absent lists; the grid wants one row per
  // employee with a cell per date.
  const allRows: DailyPayRow[] = (sheet?.employees || []).map((employee) => {
    const present = new Set(employee.present_dates || []);
    return {
      user_id: employee.user_id,
      employee_id: employee.employee_id,
      name: employee.name,
      email: employee.email,
      daily_rate: Number(
        employee.effective_daily_rate ?? employee.daily_rate_bdt ?? 0,
      ),
      entries: dates.map((date) => ({
        date,
        is_payable: present.has(date),
      })),
    };
  });

  const term = search.trim().toLowerCase();
  const rows = allRows.filter((row) =>
    term
      ? `${row.name} ${row.employee_id}`.toLowerCase().includes(term)
      : true,
  );
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pendingCount = Object.keys(pending).length;

  const handleToggleCell = (row: DailyPayRow, entry: DailyPayEntry) => {
    if (!isEditMode) return;
    const key = `${row.user_id}|${entry.date}`;
    const current = key in pending ? pending[key] : Boolean(entry.is_payable);
    setPending((prev) => ({ ...prev, [key]: !current }));
  };

  const handleSetColumn = (date: string, value: boolean) => {
    if (!isEditMode) return;
    setPending((prev) => {
      const next = { ...prev };
      pageRows.forEach((row) => {
        next[`${row.user_id}|${date}`] = value;
      });
      return next;
    });
  };

  const handleStartEdit = () => {
    if (canChange) setIsEditing(true);
  };

  const handleOpenUpload = () => {
    if (canChange) setOpenUpload(true);
  };

  const handleSave = async () => {
    if (!canChange) return;
    const updates: PerSheetDayUpdate[] = Object.entries(pending).map(
      ([key, isPresent]) => {
        const [userId, date] = key.split('|');
        return { userId, date, isPresent };
      },
    );

    if (!updates.length) {
      setIsEditing(false);
      return;
    }

    try {
      await saveSheet({ updates }).unwrap();
      setPending({});
      setIsEditing(false);
      toast('success', 'Pay sheet saved successfully');
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to save pay sheet'));
    }
  };

  const handleCancelEdit = () => {
    setPending({});
    setIsEditing(false);
  };

  /** Moving away with unsaved edits would silently drop them. */
  const guardUnsaved = (what: string) => {
    if (!pendingCount) return true;
    toast('warning', `Save or cancel your changes before changing the ${what}`);
    return false;
  };

  /**
   * Arrows move one cycle at a time. A month with a single cycle behaves like
   * plain month paging; a split month walks its cycles before crossing over.
   */
  const handleStep = (direction: -1 | 1) => {
    if (!guardUnsaved('cycle')) return;
    setIsEditing(false);
    setPage(1);

    const next = activeIndex + direction;
    if (next >= 0 && next < cycles.length) {
      setCyclePos(next);
      return;
    }
    setMonth((prev) => prev.add(direction, 'month'));
    setCyclePos(direction === 1 ? 0 : 'last');
  };

  return (
    <div className="space-y-5">
      {/* toolbar */}
      <div className="bg-theme-slate-800 border border-theme-gray-700 rounded-xl p-4">
        <Flex align="center" justify="space-between" gap={16} wrap>
          <Flex align="center" gap={12} wrap>
            <Button
              type="text"
              className="!size-9 !text-white border border-theme-gray-700 rounded"
              icon={<ChevronLeftIcon size={18} />}
              onClick={() => handleStep(-1)}
            />
            <div className="text-center min-w-[190px]">
              <div className="text-white text-base font-medium">
                {activeCycle?.label || month.format('MMMM YYYY')}
              </div>
              <div className="text-white/40 text-xs">
                {dates.length} days · daily cycle
              </div>
            </div>
            <Button
              type="text"
              className="!size-9 !text-white border border-theme-gray-700 rounded"
              icon={<ChevronRightIcon size={18} />}
              onClick={() => handleStep(1)}
            />

          </Flex>

          <Flex align="center" gap={12} wrap>
            <Input
              size="large"
              className="shrink-0 w-[225px]"
              placeholder="Search employee"
              prefix={<SearchIcon size={20} className="text-white" />}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />

            <RefreshButton
              endpoints={['getPerSheet', 'getFailedPerSheetJobs']}
            />
            <Tooltip title={canChange ? '' : LOCKED_HINT}>
              <Button
                size="middle"
                disabled={!canChange}
                icon={<UploadIcon size={18} />}
                onClick={handleOpenUpload}
                className="btn-primary-bordered !h-10"
              >
                Upload Sheet
              </Button>
            </Tooltip>

            {isEditMode ? (
              <Flex align="center" gap={8}>
                <Button
                  size="middle"
                  className="btn-primary-bordered !h-10"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  size="middle"
                  loading={isSaving}
                  onClick={handleSave}
                  className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md !h-10"
                >
                  {pendingCount
                    ? `Save ${pendingCount} change${pendingCount > 1 ? 's' : ''}`
                    : 'Save Changes'}
                </Button>
              </Flex>
            ) : (
              <Tooltip title={canChange ? '' : LOCKED_HINT}>
                <Button
                  size="middle"
                  disabled={!canChange}
                  icon={<PencilEdit02Icon size={18} />}
                  onClick={handleStartEdit}
                  className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md !h-10"
                >
                  Edit Sheet
                </Button>
              </Tooltip>
            )}
          </Flex>
        </Flex>

        <Flex align="center" gap={16} className="mt-3" wrap>
          <Flex align="center" gap={6}>
            <span className="size-3 rounded border border-theme-success-500 bg-theme-success-500/20" />
            <span className="text-white/50 text-xs">Present</span>
          </Flex>
          <Flex align="center" gap={6}>
            <span className="size-3 rounded border border-theme-error-500 bg-theme-error-500/10" />
            <span className="text-white/50 text-xs">Absent</span>
          </Flex>
          <Flex align="center" gap={6}>
            <span className="size-3 rounded border border-dashed border-theme-success-500/60 bg-theme-success-500/5" />
            <span className="text-white/50 text-xs">Edited, not saved</span>
          </Flex>

          <span className="text-white/40 text-xs ml-auto">
            {isEditMode
              ? `${sheet?.total_present ?? 0} present · ${sheet?.total_absent ?? 0} absent this month`
              : 'View only · press Edit Sheet to change'}
          </span>
        </Flex>
      </div>

      <PaySheetGrid
        days={days}
        rows={pageRows}
        loading={isLoading || isFetching}
        editing={isEditMode}
        page={page}
        limit={PAGE_SIZE}
        total={rows.length}
        pending={pending}
        onToggleCell={handleToggleCell}
        onSetColumn={handleSetColumn}
        onPaginate={(nextPage) => {
          if (guardUnsaved('page')) setPage(nextPage);
        }}
      />

      <PaySheetUpload
        open={openUpload && canChange}
        dates={dates}
        label={activeCycle?.label || month.format('MMMM YYYY')}
        rows={allRows}
        year={month.year()}
        month={month.format('MM')}
        cycleNo={cycles.length > 1 ? activeCycle?.cycleNo : undefined}
        onClose={() => setOpenUpload(false)}
      />
    </div>
  );
};

export default PaySheet;
